import { Input, InputField, Text } from '@gluestack-ui/themed';
import React from 'react';
import { FormFieldData } from '../FormField.types.ts';

/**
 * Props for the numeric form field.
 * - readonly: renders a plain Text instead of an input
 * - placeholder: optional placeholder string
 * - inputProps: props forwarded to the outer <Input>
 * - inputFieldProps: props forwarded to the inner <InputField>
 */
export interface NumericFormFieldData {
  readonly?: boolean;
  placeholder?: string;
  inputProps?: Partial<React.ComponentProps<typeof Input>>;
  inputFieldProps?: Partial<React.ComponentProps<typeof InputField>>;
}

/**
 * A form-field that only allows digits 0–9.
 * Value is still passed as string, e.g. "12345".
 */
export const NumericFormField = ({
  disabled,
  value,
  onChange,
  readonly,
  placeholder,
  size = 'md',
  inputProps,
  inputFieldProps,
}: FormFieldData<'Number'> & NumericFormFieldData) => {
  if (readonly) {
    return (
      <Text size={size} pl="$0.5">
        {value ?? 'Not specified'}
      </Text>
    );
  }

  const handleChange = (text: string) => {
    const digitsOnly = text.replace(/[^0-9]/g, '');
    onChange(digitsOnly);
  };

  return (
    <Input size={size} isDisabled={disabled} {...inputProps}>
      <InputField
        // Restrict to numeric keypad on browsers that support it:
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChangeText={handleChange}
        placeholder={placeholder}
        {...inputFieldProps}
      />
    </Input>
  );
};
